import { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Upload, Trash2, Baseline, Loader2 } from "lucide-react";
import { t } from "../i18n";

const ACCEPTED = ["ttf", "otf", "woff", "woff2"];

export function FontsModal({ open, onOpenChange, fonts = [], onChange }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of list) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!ACCEPTED.includes(ext)) {
        toast.error(t("{arquivo}: formato não suportado", { arquivo: file.name }));
        continue;
      }
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post("/fonts", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(e.response?.data?.detail || t("Falha ao enviar {arquivo}", { arquivo: file.name }));
      }
    }
    setBusy(false);
    if (ok) { toast.success(t("{n} fonte(s) enviada(s)", { n: ok })); onChange?.(); }
  };

  const del = async (id) => {
    try { await api.delete(`/fonts/${id}`); toast.success(t("Fonte removida")); onChange?.(); }
    catch { toast.error(t("Falha ao remover")); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fonts-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Baseline className="h-5 w-5 text-primary" /> {t("Fontes personalizadas")}</DialogTitle>
          <DialogDescription>{t("Envie suas fontes (.ttf, .otf, .woff, .woff2) e use-as nos textos do overlay.")}</DialogDescription>
        </DialogHeader>

        <input
          ref={inputRef}
          type="file"
          accept=".ttf,.otf,.woff,.woff2"
          multiple
          className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
          data-testid="font-file-input"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full rounded-sm"
          data-testid="font-upload-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {busy ? t("Enviando…") : t("Enviar fonte")}
        </Button>

        <div className="mt-2 max-h-64 overflow-y-auto space-y-1.5" data-testid="fonts-list">
          {fonts.length === 0 && <p className="text-center text-sm text-muted-foreground py-6">{t("Nenhuma fonte enviada ainda.")}</p>}
          {fonts.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-sm border border-border/60 bg-card px-3 py-2" data-testid={`font-row-${f.id}`}>
              <span className="text-sm truncate" style={{ fontFamily: `'${f.family}', sans-serif` }}>{f.family}</span>
              <button onClick={() => del(f.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`font-delete-${f.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
